import { useEffect, useState, useCallback } from "react";
import { Loader2, Download, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { admin, downloadCsv } from "./adminApi";

export function AdminSubscribers({ onUnauthed }) {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSubscribers((await admin.get(`/admin/subscribers`)).data.subscribers); }
    catch (err) { if (err?.response?.status === 401) onUnauthed(); }
    finally { setLoading(false); }
  }, [onUnauthed]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-subscribers-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="bl-eyebrow text-[color:var(--bl-gold)]">Email Signups</div>
          <div className="font-display text-white text-2xl mt-1">{subscribers.length} on list</div>
        </div>
        <button onClick={() => downloadCsv(`/admin/subscribers/export.csv`, "biolabz-subscribers", "subscribers")}
          data-testid="subscribers-export" className="bl-btn gold text-[11px] py-2 px-4 inline-flex items-center gap-2">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin text-white/40 mx-auto" /></div>
      ) : subscribers.length === 0 ? (
        <div className="border border-white/8 p-10 text-center">
          <Mail size={22} className="mx-auto text-white/30" />
          <div className="text-white/50 text-sm mt-4">No signups yet — the welcome popup fires after 8s or on exit-intent on first visit.</div>
        </div>
      ) : (
        <div className="border border-white/8 divide-y divide-white/8">
          {subscribers.map((s) => (
            <div key={s.email} data-testid={`sub-row-${s.email}`} className="p-4 flex flex-wrap justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm font-mono truncate">{s.email}</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">
                  {new Date(s.created_at).toLocaleString()} · via {s.first_source}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminReviews({ reviews, refresh }) {
  const approve = async (id) => {
    try {
      await admin.post(`/admin/reviews/${id}/approve`, {});
      toast.success("Review approved");
      refresh();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-reviews-panel">
      {reviews.length === 0 && <div className="text-white/40 py-8 text-center">No reviews yet</div>}
      {reviews.map(r => (
        <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-white/8 p-5 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{new Date(r.created_at).toLocaleString()} · {r.rating}★</div>
            {r.title && <div className="font-display text-white uppercase mt-1">{r.title}</div>}
            <p className="text-white/70 text-sm mt-2 leading-relaxed">{r.comment}</p>
            <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-white/40">— {r.name}</div>
          </div>
          <div className="shrink-0">
            {r.approved ? (
              <div className="inline-flex items-center gap-1 text-[color:var(--bl-green)] font-mono text-[10px] tracking-[0.25em] uppercase"><CheckCircle2 size={13} /> Approved</div>
            ) : (
              <button onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="bl-btn primary text-[10px] py-2 px-3">Approve</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
